import * as React from 'react';
import useV2Query from 'utils/useV2Query';
import { GET_TASK_COUNT_BY_JOB_TYPE } from './queries';
import { getTeamFromJobType } from 'task-manager/teamJobLookup';
import { Box, Link, Typography } from '@material-ui/core';
import { JobType, JobStatus, Team } from 'task-manager/types';
import { formatScreamingSnakesToPrettyStrings } from 'task-manager/formatScreamingSnakesToPrettyStrings';
import { useStyles } from './TaskSummary.styles';

type TaskSummaryProps = {
  clientId: string;
};

type JobTypeCount = {
  jobType: JobType;
  typeCount: number;
};

export const TaskSummary = ({ clientId }: TaskSummaryProps) => {
  const classes = useStyles();

  const { data, loading } = useV2Query<{ jobTypeCount: JobTypeCount[] }>(
    GET_TASK_COUNT_BY_JOB_TYPE,
    {
      variables: {
        clientId,
        status: JobStatus.PENDING,
      },
    },
  );

  const taskCountGroupedByTeam = React.useMemo<Record<string, number>>(
    () => (data ? aggregateCountsByTeam(data.jobTypeCount) : initializeEmptyTeamCounts()),
    [data],
  );

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.titleContainer}>
        <Typography variant="h2" className={classes.title}>
          Oustanding Tasks
        </Typography>
        <Typography className={classes.description}>
          Count of &apos;To do&apos; and &apos;Paused&apos; tasks for this client, grouped by teams.{' '}
          <Link
            href="https://benchco.atlassian.net/wiki/spaces/OPS2/pages/3867803681/Specialization+Task+Mapping"
            target="_blank"
          >
            Learn More
          </Link>
        </Typography>
      </Box>
      <Box className={classes.teamContainer}>
        {Object.entries(taskCountGroupedByTeam).map(([team, count]) => (
          <Box key={team} className={classes.team}>
            <span>{formatScreamingSnakesToPrettyStrings(team)}</span>
            <span>{count}</span>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

function aggregateCountsByTeam(jobTypeCounts: JobTypeCount[]) {
  const teamCounts = initializeEmptyTeamCounts();

  for (const { jobType, typeCount } of jobTypeCounts) {
    const team = getTeamFromJobType(jobType);
    if (!team) continue;
    teamCounts[team] += typeCount;
  }

  return teamCounts;
}

function initializeEmptyTeamCounts() {
  return Object.keys(Team).reduce((acc, team) => {
    acc[team] = 0;
    return acc;
  }, {});
}
